/**
 * The core bridge for a blend: blending session document → `evaluateBlend` input.
 *
 * `calculation.ts` beside this file does the same job for a recipe, and this is
 * here for the same reason: it is the one translation between the document
 * model and core's input, and **two callers in two runtimes need it**. The
 * browser evaluates the session the blender is typing into and the strip that
 * follows them down the page; the Worker's assistant renders a blend for the
 * model. It lived in `apps/web/src/lib/blending.ts` and again, byte for byte,
 * in `apps/web/src/worker/ai/render.ts` — a copy the maths would eventually
 * have drifted through, since a new addition kind changes this function and
 * nothing would have failed if only one copy learned about it.
 *
 * Nothing is computed here. What the mapping *does* decide is which rows reach
 * the maths at all, and that is a rule rather than a detail (D-BS1):
 *
 * - A **fruit** row and a **spirit** row each carry two numbers, and a row
 *   holding one of them is dropped. Half a row would quietly move the ABV or
 *   the sugar by an amount the brewer never typed, and a blender who has
 *   entered the volume but not yet the gravity is mid-sentence rather than
 *   making a claim. This is the rule the single `fruits` row always kept.
 * - A **sugar** row goes through once it has been weighed, and is dropped
 *   until then. Its other field, which sugar it is, is always set — the row is
 *   born naming one — so the weight is the whole question.
 * - A **whole-fruit** row needs both its numbers, the weight and the sugar
 *   percent, and is dropped without either: a bucket nobody has weighed moves
 *   nothing, and a weight with no percent beside it is a brewer part-way
 *   through the row rather than a claim about how sweet the fruit was. The
 *   `fruit` id does not travel — it names where the percent came from, and the
 *   maths only ever reads the percent, which the brewer may have overwritten.
 * - A **flavour** row always goes through, whatever it holds, because there is
 *   no number the maths wants from it: oak, coriander and a dry hop change what
 *   the beer tastes like and not what it measures.
 *
 * A component with no volume yet maps to `0` rather than being dropped, which
 * keeps every volume-weighted figure total: core divides by the total volume,
 * and a card nobody has measured contributes nothing to either side of it.
 */

import type { BlendAdditionInput, BlendInput } from '@beerwright/core';
import { type BlendDoc, latestBlendReading } from '../document/blend';

/** How a caller says what "now" is, for a blend that is not packaged yet. */
export interface BlendInputOptions {
  /**
   * Today, from the caller's clock. Omitted, the clock is read here instead.
   *
   * Only ever used as the packaging day of a blend that has not reached one
   * (D-BS12), which turns the addition-timing question into the one a blender
   * standing over the vessel is actually asking: *if I bottled this now, what
   * is still sugar?*
   *
   * It used to default to no packaging day at all, and that was a convention a
   * call site had to remember: of seven callers exactly one passed it, so the
   * same blend printed one carbonation figure on its own page and a different
   * one in its journal, on a printed tasting card, on the public page a taster
   * scans and in both assistant renders (PLAN-UX-PASS A3). What is left is an
   * injection point a test can pin, rather than a question a caller can get
   * wrong — the default is "now", so nobody can accidentally get `null`.
   *
   * The clock is read inside {@link blendInputOf}, at call time, and never at
   * module scope: this package is loaded by the Worker, where a module-scope
   * `new Date()` reads the Unix epoch.
   */
  readonly asOf?: Date;
}

/** `YYYY-MM-DD` to an instant, or `null`. */
function dayToDate(day: string | null): Date | null {
  return day === null ? null : new Date(day);
}

/** The calculator input for one persisted session. */
export function blendInputOf(doc: BlendDoc, options: BlendInputOptions = {}): BlendInput {
  const latest = latestBlendReading(doc.readings);
  return {
    components: doc.components.map((component) => ({
      id: component.componentId,
      name: component.name,
      volumeL: component.volumeL ?? 0,
      og: component.og,
      fg: component.fg,
      ph: component.ph,
      srm: component.srm,
      peakTempC: component.peakTempC,
      barrelAged: component.barrelAged,
      naohMl: component.naohMl,
    })),
    targetVolumes: doc.targetVolumes,
    // A flavour has no number the maths wants, so it always goes through; a
    // sugar nobody has weighed, or a fruit, a bucket of whole fruit or a
    // spirit missing one of its two, is dropped, as fruit rows always have
    // been — half a row would quietly move the ABV.
    additions: doc.additions.flatMap((item): BlendAdditionInput[] => {
      const rows = coreRows(item);
      // Attached after the mapping and only when there is one, so an undated
      // row crosses this bridge as exactly the object it always did (D-BS12).
      const addedOn = dayToDate(item.addedOn);
      return addedOn === null ? rows : rows.map((row) => ({ ...row, addedOn }));
    }),
    kegVolumeL: doc.kegVolumeL,
    sugar: doc.sugar,
    // The latest reading with a gravity on it, and only that one: the earlier
    // rows are the record of the blend drying out and the page draws them, but
    // the arithmetic wants the gravity in the vessel now (D-BS12). Same shape
    // as insights taking the most recent scored tasting off a batch.
    measuredFg: latest?.fg ?? null,
    measuredOn: dayToDate(latest?.on ?? null),
    // The day the sugar stops having time to ferment: the day it was packaged,
    // or — for a blend still in the vessel — today, so the question stays "if I
    // bottled this now". Today is the default rather than something a caller
    // supplies, because six of the seven callers did not and the blend drew
    // itself differently in each of them (PLAN-UX-PASS A3). The clock is read
    // here, at call time, and not at module scope. `removedOn` never travels:
    // it is recorded, not read.
    //
    // One case reads "today" where it means something else: `blend.setStage`
    // takes an optional `on`, so a blend can be *packaged* with no packaging
    // day — the assistant proposes exactly that when it moves a stage without
    // being told a date. Such a blend falls through to today, which for one
    // packaged weeks ago under-counts the rows that were still sugar when the
    // caps actually went on. Deliberately left as it is: the blend page has
    // always read it this way, and A3 is about every surface agreeing rather
    // than about inventing a date nobody wrote down.
    packagedOn: dayToDate(doc.stageDates.packaged) ?? options.asOf ?? new Date(),
  };
}

/** One document row as core's inputs, or none where the row is still half-typed. */
function coreRows(item: BlendDoc['additions'][number]): BlendAdditionInput[] {
  // A flavour and a hop both always go through, whatever they hold: neither
  // has a number the maths wants, so there is no half-typed row to guard
  // against (D-BS1, D-BS13).
  if (item.kind === 'flavour' || item.kind === 'hop') {
    return [{ kind: item.kind, name: item.name, amountG: item.amountG }];
  }
  if (item.kind === 'sugar') {
    return item.massKg === null
      ? []
      : [{ kind: 'sugar', name: item.name, massKg: item.massKg, sugar: item.sugar }];
  }
  if (item.kind === 'whole-fruit') {
    return item.massKg === null || item.sugarPercent === null
      ? []
      : [
          {
            kind: 'whole-fruit',
            name: item.name,
            massKg: item.massKg,
            sugarPercent: item.sugarPercent,
          },
        ];
  }
  if (item.volumeL === null) return [];
  if (item.kind === 'fruit') {
    return item.sg === null
      ? []
      : [{ kind: 'fruit', name: item.name, volumeL: item.volumeL, sg: item.sg }];
  }
  return item.abvPercent === null
    ? []
    : [
        {
          kind: 'spirit',
          name: item.name,
          volumeL: item.volumeL,
          abvPercent: item.abvPercent,
        },
      ];
}
